import { useUIComponentsContext } from '../../Provider/UiComponentsProvider'
import Tooltip from '../../Tooltip'
import { useAccessContext } from '../AccessProvider'
import { AccessAccount, UserAccess } from '../types'
import { CONTRIBUTOR_RIGHTS } from '../utils'
import ContributorMoreActions from './ContributorMoreActions'
import OwnerMoreActions from './OwnerMoreActions'
import UserAccessDropdown from './UserAccessDropdown'

interface IUserContributorProps {
  account: AccessAccount
  access: UserAccess
  teamId?: string
  disabled?: boolean
  showMoreActions?: boolean
  isProjectResponsible?: boolean
  disableAccessDropdown?: boolean
  onPermissionChange?: Function
  onContributorDelete?: Function
  onOwnerChange?: (newOwner: AccessAccount) => void
  showPermissionEdit?: boolean
}

const UserContributor = ({
  account,
  access,
  teamId,
  showMoreActions = true,
  disabled = false,
  onPermissionChange,
  onContributorDelete,
  isProjectResponsible,
  disableAccessDropdown,
  onOwnerChange,
  showPermissionEdit = true,
}: IUserContributorProps) => {
  const { t } = useUIComponentsContext()

  const { UserAvatar, teamContributors } = useAccessContext()

  const contributorRights = CONTRIBUTOR_RIGHTS(t)

  const { id: userId, fullName, jobTitle } = account

  const isOwner = access === contributorRights.OWNER.value

  const actionProps = teamId ? { userId, teamId } : { userId }

  const shouldDisable = disabled || isProjectResponsible

  const actionItems = [
    {
      id: 0,
      label: (
        <span className="text-watermelon-500">{t?.('buttons.delete')}</span>
      ),
      action: () => onContributorDelete && onContributorDelete(actionProps),
    },
  ]

  const inheritsEditorAccess =
    access === contributorRights.VIEWER.value &&
    teamContributors?.find((team) =>
      team.contributors?.find(
        (c) =>
          c?.account?.id === account?.id &&
          c?.access === contributorRights.EDITOR.value
      )
    )

  return (
    <div className="flex justify-between">
      <div className="flex items-center w-2/3 mr-2">
        <div className="mr-2">
          <UserAvatar name={fullName} accountId={userId} />
        </div>
        <div className="flex flex-col font-sm text-oxford-gray-800">
          <span className="font-semibold text-sm truncate">{fullName}</span>
          <span className="text-xs">{jobTitle?.name}</span>
        </div>
      </div>

      <div className="flex">
        {!isOwner && (
          <div className="flex justify-center items-center mr-3">
            {inheritsEditorAccess && (
              <Tooltip content={t?.('access.userInheritsEditorRightsFromTeam')}>
                <i className="fas fa-exclamation-triangle text-lg leading-none text-candlelight-500 mr-2" />
              </Tooltip>
            )}

            {showPermissionEdit && (
              <div className="min-w-[140px]">
                <UserAccessDropdown
                  allowSearch={false}
                  paddingClass="px-3 py-1"
                  value={
                    isOwner || isProjectResponsible
                      ? contributorRights.EDITOR
                      : contributorRights[access]
                  }
                  onChange={({ value }) =>
                    onPermissionChange?.({ value, ...actionProps })
                  }
                  dataParser={(data) =>
                    data.filter(
                      ({ value }) =>
                        value !== access &&
                        value !== contributorRights.OWNER.value
                    )
                  }
                  disabled={disableAccessDropdown || shouldDisable}
                />
              </div>
            )}
          </div>
        )}
        <div className="flex justify-center items-center w-[48px]">
          {showMoreActions && !shouldDisable ? (
            isOwner ? (
              <OwnerMoreActions
                ownerId={account?.id}
                onOwnerChange={onOwnerChange}
                disabled={disabled}
              />
            ) : (
              <ContributorMoreActions
                actionItems={actionItems}
                disabled={disabled || isProjectResponsible}
              />
            )
          ) : (
            <div className="w-[48px]" />
          )}
        </div>
      </div>
    </div>
  )
}

export default UserContributor
